import React from "react";
import SelectDropdown from "../../../../library/SelectDropdown";
import ToggleSwitch from "../../../../library/ToggleSwitch";
import {
    StyledPanel,
    StyledPanelBody,
    StyledPanelSection,
    StyledPanelTitle,
    StyledSectionDivider,
    StyledStudentToggleGrid,
    StyledToggleGrid,
} from "./VersionDetailsForm.styles";

/**
 * Version Details' right-hand panel (SPEC2 R5/R6): "Statement of Results
 * Settings" (SOR Template, show-x toggles, Override SOR Brand) and, below a
 * divider, "Student Portal Settings" - one card per node 164:120843, not
 * two. AP-1218 owns these settings' own behaviour; this screen only owns
 * placement/persistence. Every control disables (with a reason) when locked
 * or view-only; saved values still render while disabled.
 * @param {boolean} isVersionLocked Whether the version's settings are locked.
 * @param {boolean} hasEditAccess Whether the user has edit (not view-only) access.
 */
function StatementOfResultsPanel({ formik, sorTemplateOptions, brandOptions, isVersionLocked, hasEditAccess }) {
    const fieldsDisabled = isVersionLocked || !hasEditAccess;
    const disabledReason = isVersionLocked ? "This version's settings are locked" : "Insufficient Permissions";

    return (
        <StyledPanel>
            <StyledPanelSection>
                <StyledPanelTitle>Statement of Results Settings</StyledPanelTitle>

                <StyledPanelBody>
                    <SelectDropdown
                        label="SOR Template"
                        placeholder="Select SOR Template"
                        id="st_template"
                        name="st_template"
                        value={formik.values.st_template}
                        onChange={(event) => formik.setFieldValue("st_template", event.target.value)}
                        options={sorTemplateOptions}
                        clearable
                        disabled={fieldsDisabled}
                        disabledReason={disabledReason}
                    />

                    <StyledToggleGrid>
                        <ToggleSwitch
                            id="show_empty_grades"
                            name="show_empty_grades"
                            label="Show Empty Grades"
                            checked={formik.values.show_empty_grades === 1}
                            onChange={(event) => formik.setFieldValue("show_empty_grades", event.target.checked ? 1 : 0)}
                            disabled={fieldsDisabled}
                            disabledReason={disabledReason}
                        />

                        <ToggleSwitch
                            id="show_final_results"
                            name="show_final_results"
                            label="Show Final Results"
                            checked={formik.values.show_final_results === 1}
                            onChange={(event) => formik.setFieldValue("show_final_results", event.target.checked ? 1 : 0)}
                            disabled={fieldsDisabled}
                            disabledReason={disabledReason}
                        />

                        <ToggleSwitch
                            id="show_assessments"
                            name="show_assessments"
                            label="Show Assessments"
                            checked={formik.values.show_assessments === 1}
                            onChange={(event) => formik.setFieldValue("show_assessments", event.target.checked ? 1 : 0)}
                            disabled={fieldsDisabled}
                            disabledReason={disabledReason}
                        />

                        <ToggleSwitch
                            id="override_sor_brand"
                            name="override_sor_brand"
                            label="Override SOR Brand"
                            checked={formik.values.override_sor_brand === 1}
                            onChange={(event) => formik.setFieldValue("override_sor_brand", event.target.checked ? 1 : 0)}
                            disabled={fieldsDisabled}
                            disabledReason={disabledReason}
                        />
                    </StyledToggleGrid>

                    {formik.values.override_sor_brand === 1 && (
                        <SelectDropdown
                            label="Brand"
                            placeholder="Select Brand"
                            id="override_brand"
                            name="override_brand"
                            value={formik.values.override_brand}
                            onChange={(event) => formik.setFieldValue("override_brand", event.target.value)}
                            options={brandOptions}
                            clearable
                            disabled={fieldsDisabled}
                            disabledReason={disabledReason}
                        />
                    )}
                </StyledPanelBody>
            </StyledPanelSection>

            <StyledSectionDivider />

            <StyledPanelSection>
                <StyledPanelTitle>Student Portal Settings</StyledPanelTitle>

                <StyledPanelBody>
                    <StyledStudentToggleGrid>
                        <ToggleSwitch
                            id="allow_student_portal_gradebook_view"
                            name="allow_student_portal_gradebook_view"
                            label="Student Gradebook View"
                            description="Allow students to view their gradebook on their student portal."
                            checked={formik.values.allow_student_portal_gradebook_view === 1}
                            onChange={(event) =>
                                formik.setFieldValue("allow_student_portal_gradebook_view", event.target.checked ? 1 : 0)
                            }
                            disabled={fieldsDisabled}
                            disabledReason={disabledReason}
                        />

                        <ToggleSwitch
                            id="allow_student_portal_sor_to_download"
                            name="allow_student_portal_sor_to_download"
                            label="Student SOR Download"
                            description="Allow students to download their statement of results on their student portal."
                            checked={formik.values.allow_student_portal_sor_to_download === 1}
                            onChange={(event) =>
                                formik.setFieldValue("allow_student_portal_sor_to_download", event.target.checked ? 1 : 0)
                            }
                            disabled={fieldsDisabled}
                            disabledReason={disabledReason}
                        />
                    </StyledStudentToggleGrid>
                </StyledPanelBody>
            </StyledPanelSection>
        </StyledPanel>
    );
}

export default StatementOfResultsPanel;
